import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

const CUSTOM_FILTER_PAGE_HIDDEN_CLASS = 'custom-filter-page-hidden';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLAnchorElement} [loadMoreButton] - The load more button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {Set<number>} */
  #renderedPages = new Set();

  #isLoadingMore = false;
  #filterUpdateObserver = null;
  #filterUpdateTimeout = null;
  #pageLoadPromise = null;
  #loadAllPagesRequested = false;
  #loadGeneration = 0;
  #isFilterUpdating = false;
  #customFilterSnapshot = null;
  #customFilterSignature = '';
  #customFilterVisibleLimit = 0;
  #customFilterBatchSize = 0;
  #customFilterTotal = 0;
  #customFilterReady = false;
  #customFilterLoadError = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#initializeRenderedPages();
    this.#refreshPaginationState();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#clearFilterUpdateWatchers();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  get paginationMode() {
    const mode = this.getAttribute('pagination-mode');
    if (mode === 'load-more') return 'load-more';
    if (mode === 'pagination') return 'pagination';
    return 'infinite';
  }

  loadMore(event) {
    if (this.paginationMode !== 'load-more') return;
    if (event instanceof Event) event.preventDefault();

    if (this.#customFilterLoadError && this.#hasActiveCustomFilters()) {
      this.#customFilterLoadError = false;
      this.#customFilterSignature = '';
      return this.loadAllPagesForCustomFilter();
    }

    if (this.#customFilterReady && this.#hasActiveCustomFilters()) {
      this.#showMoreCustomFilterResults();
      return Promise.resolve();
    }

    return this.#queuePageLoad();
  }

  /**
   * Loads every collection page only while a custom collection filter is active.
   */
  loadAllPagesForCustomFilter() {
    if (this.paginationMode !== 'load-more') return Promise.resolve();

    const signature = this.#getCustomFilterSignature();
    if (signature === this.#customFilterSignature) {
      return this.#pageLoadPromise || Promise.resolve();
    }

    this.#customFilterSignature = signature;
    this.#customFilterVisibleLimit = 0;
    this.#customFilterReady = false;
    this.#customFilterLoadError = false;
    this.#loadGeneration += 1;
    this.#loadAllPagesRequested = true;
    if (this.#isFilterUpdating) return Promise.resolve();

    return this.#queuePageLoad();
  }

  async #queuePageLoad() {
    if (this.#pageLoadPromise) return this.#pageLoadPromise;

    this.#pageLoadPromise = this.#drainPageLoads();

    try {
      return await this.#pageLoadPromise;
    } finally {
      this.#pageLoadPromise = null;
    }
  }

  async #drainPageLoads() {
    do {
      const loadAllPages = this.#loadAllPagesRequested;
      this.#loadAllPagesRequested = false;

      if (loadAllPages) {
        await this.#renderAllPagesForCustomFilter();
      } else {
        await this.#renderNextPage();
      }
    } while (this.#loadAllPagesRequested && !this.#isFilterUpdating);
  }

  #observeViewMore() {
    if (this.paginationMode !== 'infinite') {
      this.infinityScrollObserver?.disconnect();
      return;
    }

    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @param {number} [generation] - The current native-filter generation
   */
  async #fetchSpecificPage(pageNumber, url = undefined, generation = this.#loadGeneration) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo) || this.pages.has(pageNumber) || this.#renderedPages.has(pageNumber)) return false;

    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
    const parsedGrid = parsedPage.querySelector('[ref="grid"]');
    const renderedPage = Number(parsedGrid?.dataset.currentPage);
    const hasExpectedCards = parsedGrid?.querySelector(':scope > [ref="cards[]"]');
    if (!parsedGrid || renderedPage !== pageNumber || !hasExpectedCards) {
      sectionRenderer.invalidateSectionHTML(this.sectionId, pageInfo.url);
      if (generation !== this.#loadGeneration || this.#isFilterUpdating) return false;
      throw new Error(`Unable to load collection page ${pageNumber}: invalid product grid`);
    }

    if (generation !== this.#loadGeneration || this.#isFilterUpdating) return false;

    this.pages.set(pageNumber, pageContent);
    return true;
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.paginationMode === 'pagination') return;
    if (this.#isFilterUpdating) return;
    if (this.paginationMode === 'load-more' && this.#isLoadingMore) return;

    if (!this.#hasNextPage()) {
      this.#syncLoadMoreButton();
      return;
    }

    if (this.paginationMode === 'load-more') {
      this.#isLoadingMore = true;
      this.#syncLoadMoreButton(true);
    }

    const generation = this.#loadGeneration;
    const visibleCountBefore = this.#getVisibleItemsCount();
    let lastRenderedPage;

    try {
      while (this.#hasNextPage()) {
        const nextPage = this.#getPage('next');
        if (!nextPage || !this.#shouldUsePage(nextPage) || this.#renderedPages.has(nextPage.page)) break;

        let nextPageItemElements = this.#getGridForPage(nextPage.page);

        if (!nextPageItemElements) {
          await this.#fetchSpecificPage(nextPage.page, nextPage.url, generation);
          if (generation !== this.#loadGeneration || this.#isFilterUpdating || !this.isConnected) break;

          nextPageItemElements = this.#getGridForPage(nextPage.page);
          if (!nextPageItemElements) break;
        }

        grid.append(...nextPageItemElements);
        this.#renderedPages.add(nextPage.page);
        this.#setCurrentPage(nextPage.page);
        lastRenderedPage = nextPage;

        this.#aspectRatioHelper?.processNewElements();
        await this.#waitForClientSideFilters();
        this.#updateVisibleCount();

        if (this.paginationMode !== 'load-more') break;
        if (!this.#shouldAutoAdvanceHiddenPages(visibleCountBefore)) break;
      }

      if (this.paginationMode === 'infinite' && lastRenderedPage) {
        history.pushState('', '', lastRenderedPage.url.toString());
        requestIdleCallback(() => {
          this.#fetchPage('next');
        });
      } else {
        this.#syncLoadMoreButton();
      }
    } finally {
      if (this.paginationMode === 'load-more') {
        this.#isLoadingMore = false;
        this.#syncLoadMoreButton();
      }
    }
  }

  async #renderAllPagesForCustomFilter() {
    const { grid } = this.refs;

    if (!(grid instanceof HTMLElement) || this.paginationMode !== 'load-more' || this.#isLoadingMore || this.#isFilterUpdating) return;
    if (!this.#hasActiveCustomFilters()) {
      this.#restoreCustomFilterSnapshot();
      return;
    }

    if (!this.#customFilterSnapshot) {
      this.#customFilterSnapshot = {
        cards: Array.from(grid.children),
        renderedPages: new Set(this.#renderedPages),
        currentPage: grid.dataset.currentPage || '1',
      };
    }

    grid.querySelectorAll(`.${CUSTOM_FILTER_PAGE_HIDDEN_CLASS}`).forEach((card) => {
      card.classList.remove(CUSTOM_FILTER_PAGE_HIDDEN_CLASS);
    });

    const renderedPages = Array.from(this.#renderedPages).filter(Number.isFinite).sort((a, b) => a - b);
    const fallbackPage = Number(grid.dataset.currentPage) || 1;
    const firstRenderedPage = renderedPages[0] || fallbackPage;
    const lastRenderedPage = renderedPages[renderedPages.length - 1] || fallbackPage;
    const lastPage = Number(grid.dataset.lastPage);

    if (!Number.isFinite(lastPage) || lastPage < 1) return;

    const generation = this.#loadGeneration;
    // 先收集所有页面节点，通过代际校验后再批量写入，避免逐页触发 DOM 和筛选扫描。
    const prependCards = [];
    const appendCards = [];
    const loadedPages = [];
    this.#isLoadingMore = true;
    this.#syncLoadMoreButton(true);

    const collectPage = async (pageNumber, position) => {
      if (!this.#hasActiveCustomFilters() || generation !== this.#loadGeneration || this.#isFilterUpdating) return false;

      let pageItemElements = this.#getGridForPage(pageNumber);
      if (!pageItemElements) {
        await this.#fetchSpecificPage(pageNumber, undefined, generation);
        if (generation !== this.#loadGeneration || this.#isFilterUpdating || !this.isConnected || !this.#hasActiveCustomFilters()) return false;

        pageItemElements = this.#getGridForPage(pageNumber);
        if (!pageItemElements) return false;
      }

      if (position === 'prepend') {
        prependCards.unshift(...pageItemElements);
      } else {
        appendCards.push(...pageItemElements);
      }

      loadedPages.push(pageNumber);
      return true;
    };

    try {
      for (let page = firstRenderedPage - 1; page >= 1; page -= 1) {
        if (!(await collectPage(page, 'prepend'))) return;
      }

      for (let page = lastRenderedPage + 1; page <= lastPage; page += 1) {
        if (!(await collectPage(page, 'append'))) return;
      }

      if (generation !== this.#loadGeneration || this.#isFilterUpdating || !this.isConnected || !this.#hasActiveCustomFilters()) return;

      if (prependCards.length) grid.prepend(...prependCards);
      if (appendCards.length) grid.append(...appendCards);
      loadedPages.forEach((page) => this.#renderedPages.add(page));

      this.#setCurrentPage(Math.max(...this.#renderedPages));
      if (loadedPages.length) this.#aspectRatioHelper?.processNewElements();
      await this.#waitForClientSideFilters();

      if (generation !== this.#loadGeneration || this.#isFilterUpdating || !this.#hasActiveCustomFilters()) return;

      this.#applyCustomFilterPagination();
    } catch (error) {
      if (generation === this.#loadGeneration && !this.#isFilterUpdating && this.#hasActiveCustomFilters()) {
        this.#customFilterLoadError = true;
      }
      throw error;
    } finally {
      this.#isLoadingMore = false;
      this.#syncLoadMoreButton();
    }
  }

  #applyCustomFilterPagination() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement) || !this.#customFilterSnapshot) return;

    const cards = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    const pageCounts = new Map();

    cards.forEach((card) => {
      card.classList.remove(CUSTOM_FILTER_PAGE_HIDDEN_CLASS);

      const page = card instanceof HTMLElement ? card.dataset.page || '' : '';
      pageCounts.set(page, (pageCounts.get(page) || 0) + 1);
    });

    this.#customFilterBatchSize = Math.max(1, ...pageCounts.values());
    if (!this.#customFilterVisibleLimit) {
      this.#customFilterVisibleLimit = this.#customFilterBatchSize;
    }

    const matchingCards = cards.filter((card) => !card.classList.contains('filtered-out'));
    this.#customFilterTotal = matchingCards.length;

    if (!(this.refs.loadMoreButton instanceof HTMLElement)) {
      this.#customFilterVisibleLimit = this.#customFilterTotal;
    }

    matchingCards.forEach((card, index) => {
      card.classList.toggle(CUSTOM_FILTER_PAGE_HIDDEN_CLASS, index >= this.#customFilterVisibleLimit);
    });

    this.dataset.customFilterTotal = String(this.#customFilterTotal);
    this.#customFilterLoadError = false;
    this.#customFilterReady = true;
    this.#updateVisibleCount();
    window.customCollectionFilters?.refresh?.();
  }

  #showMoreCustomFilterResults() {
    if (!this.#customFilterReady || !this.#customFilterBatchSize) return;

    this.#customFilterVisibleLimit += this.#customFilterBatchSize;
    this.#applyCustomFilterPagination();
    this.#syncLoadMoreButton();
  }

  #restoreCustomFilterSnapshot() {
    const { grid } = this.refs;
    const snapshot = this.#customFilterSnapshot;
    if (!(grid instanceof HTMLElement) || !snapshot) return;

    snapshot.cards.forEach((card) => card.classList.remove(CUSTOM_FILTER_PAGE_HIDDEN_CLASS));
    grid.replaceChildren(...snapshot.cards);

    this.#renderedPages.clear();
    snapshot.renderedPages.forEach((page) => this.#renderedPages.add(page));
    grid.dataset.currentPage = snapshot.currentPage;

    this.#customFilterSnapshot = null;
    this.#customFilterVisibleLimit = 0;
    this.#customFilterBatchSize = 0;
    this.#customFilterTotal = 0;
    this.#customFilterReady = false;
    this.#customFilterLoadError = false;
    delete this.dataset.customFilterTotal;

    this.#aspectRatioHelper?.processNewElements();
    this.#updateVisibleCount();
    this.#syncLoadMoreButton();
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;
    if (this.paginationMode !== 'infinite') return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage) || this.#renderedPages.has(previousPage.page)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);
    this.#renderedPages.add(previousPage.page);
    this.#setCurrentPage(previousPage.page);

    this.#aspectRatioHelper?.processNewElements();
    this.#updateVisibleCount();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards, grid } = this.refs;
    const isPrevious = type === 'previous';
    const renderedPages = Array.from(this.#renderedPages).filter(Number.isFinite).sort((a, b) => a - b);
    let currentCardPage;

    if (renderedPages.length) {
      currentCardPage = renderedPages[isPrevious ? 0 : renderedPages.length - 1];
    } else if (grid instanceof HTMLElement) {
      currentCardPage = Number(grid.dataset.currentPage);
    }

    if (!Number.isFinite(currentCardPage)) {
      if (!Array.isArray(cards)) return;

      const targetCard = cards[isPrevious ? 0 : cards.length - 1];

      if (!targetCard) return;

      currentCardPage = Number(targetCard.dataset.page);
    }

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#loadGeneration += 1;
    this.#isFilterUpdating = true;
    this.#customFilterSnapshot = null;
    this.#customFilterSignature = '';
    this.#customFilterVisibleLimit = 0;
    this.#customFilterBatchSize = 0;
    this.#customFilterTotal = 0;
    this.#customFilterReady = false;
    this.#customFilterLoadError = false;
    delete this.dataset.customFilterTotal;
    this.pages.clear();
    this.#renderedPages.clear();
    this.#isLoadingMore = false;
    this.#clearFilterUpdateWatchers();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    this.infinityScrollObserver?.disconnect();

    this.#filterUpdateObserver = new MutationObserver(() => {
      this.#clearFilterUpdateWatchers();
      this.#finishFilterUpdate();
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      this.#filterUpdateObserver.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      this.#filterUpdateTimeout = window.setTimeout(() => {
        this.#clearFilterUpdateWatchers();
        this.#finishFilterUpdate();
      }, 3000);
    } else {
      this.#finishFilterUpdate();
    }
  };

  #finishFilterUpdate() {
    if (!this.isConnected) return;

    this.#initializeRenderedPages();
    this.#isFilterUpdating = false;
    this.#refreshPaginationState();

    if (this.#loadAllPagesRequested && this.#hasActiveCustomFilters()) {
      void this.#queuePageLoad().catch((error) => {
        console.error('Unable to load all filtered collection pages.', error);
      });
    }
  }

  #initializeRenderedPages() {
    this.#renderedPages.clear();

    const { cards } = this.refs;
    if (!Array.isArray(cards)) {
      this.#updateVisibleCount();
      return;
    }

    cards.forEach((card) => {
      const page = Number(card.dataset.page);
      if (Number.isFinite(page)) {
        this.#renderedPages.add(page);
      }
    });

    this.#updateVisibleCount();
  }

  #refreshPaginationState() {
    if (this.paginationMode === 'infinite') {
      this.#observeViewMore();
      this.#fetchPage('next');
      this.#fetchPage('previous');
    } else if (this.paginationMode === 'load-more') {
      this.infinityScrollObserver?.disconnect();
      this.#syncLoadMoreButton();
    } else {
      this.infinityScrollObserver?.disconnect();
    }
  }

  #setCurrentPage(page) {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement) || !Number.isFinite(page)) return;
    grid.dataset.currentPage = String(page);
  }

  #syncLoadMoreButton(isLoading = this.#isLoadingMore) {
    const { loadMoreButton } = this.refs;
    if (!(loadMoreButton instanceof HTMLElement)) return;

    const nextPage = this.#getPage('next');
    const isRetry = this.#customFilterLoadError && this.#hasActiveCustomFilters();
    const hasNextPage = isRetry || (this.#customFilterReady
      ? this.#customFilterVisibleLimit < this.#customFilterTotal
      : this.#hasNextPage(nextPage));
    const labelElement = loadMoreButton.querySelector('.collection-results-more__label');
    const defaultLabel = loadMoreButton.dataset.defaultLabel || 'Load More Products';
    const loadingLabel = loadMoreButton.dataset.loadingLabel || 'Loading...';
    const retryLabel = loadMoreButton.dataset.retryLabel || 'Retry Loading Products';

    loadMoreButton.hidden = !hasNextPage;
    loadMoreButton.classList.toggle('is-loading', !isRetry && isLoading && hasNextPage);
    loadMoreButton.setAttribute('aria-busy', !isRetry && isLoading && hasNextPage ? 'true' : 'false');
    loadMoreButton.setAttribute('aria-disabled', !isRetry && isLoading && hasNextPage ? 'true' : 'false');

    if (!this.#customFilterReady && loadMoreButton instanceof HTMLAnchorElement && nextPage) {
      loadMoreButton.href = nextPage.url.toString();
    }

    if (labelElement instanceof HTMLElement) {
      labelElement.textContent = isRetry ? retryLabel : isLoading && hasNextPage ? loadingLabel : defaultLabel;
    }
  }

  #updateVisibleCount() {
    const { grid } = this.refs;
    const statsElement = this.querySelector('[data-collection-products-stats]');
    const visibleCountElement = statsElement?.querySelector('[data-products-stats-visible]');
    const totalCountElement = statsElement?.querySelector('[data-products-stats-total]');

    if (!(grid instanceof HTMLElement) || !(statsElement instanceof HTMLElement) || !(visibleCountElement instanceof HTMLElement)) {
      return;
    }

    const visibleItems = Array.from(grid.querySelectorAll('.product-grid__item')).filter(
      (item) => item instanceof HTMLElement && window.getComputedStyle(item).display !== 'none'
    ).length;

    const labelOne = statsElement.dataset.labelOne || '1 item';
    const labelOther = statsElement.dataset.labelOther || '2 items';
    const template = visibleItems === 1 ? labelOne : labelOther;
    let totalCount = this.#customFilterReady
      ? this.#customFilterTotal
      : Number(statsElement.dataset.totalCount || visibleItems);
    if (!this.#customFilterReady && this.paginationMode === 'load-more' && this.#hasActiveCustomFilters() && !this.#hasNextPage()) {
      totalCount = visibleItems;
    }
    const totalTemplate = totalCount === 1 ? labelOne : labelOther;
    const hasMoreItems = this.paginationMode === 'load-more' && Number.isFinite(totalCount) && totalCount > visibleItems;

    visibleCountElement.textContent = hasMoreItems ? `1~${template.replace(/\d+/, String(visibleItems))}` : template.replace(/\d+/, String(visibleItems));

    if (totalCountElement instanceof HTMLElement) {
      totalCountElement.textContent = totalTemplate.replace(/\d+/, String(totalCount));
    }
  }

  #clearFilterUpdateWatchers() {
    this.#filterUpdateObserver?.disconnect();
    this.#filterUpdateObserver = null;

    if (this.#filterUpdateTimeout) {
      window.clearTimeout(this.#filterUpdateTimeout);
      this.#filterUpdateTimeout = null;
    }
  }

  #hasNextPage(pageInfo = this.#getPage('next')) {
    return !!pageInfo && this.#shouldUsePage(pageInfo) && !this.#renderedPages.has(pageInfo.page);
  }

  #getVisibleItemsCount() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return 0;

    return Array.from(grid.querySelectorAll('.product-grid__item')).filter(
      (item) => item instanceof HTMLElement && window.getComputedStyle(item).display !== 'none'
    ).length;
  }

  #hasActiveCustomFilters() {
    const selectedFilters = window.customCollectionFilters?.state?.selectedFilters;
    return !!selectedFilters && Object.keys(selectedFilters).length > 0;
  }

  #getCustomFilterSignature() {
    const selectedFilters = window.customCollectionFilters?.state?.selectedFilters || {};

    return Object.entries(selectedFilters)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([key, value]) => `${key}:${value}`)
      .join('|');
  }

  #shouldAutoAdvanceHiddenPages(visibleCountBefore) {
    if (!this.#hasActiveCustomFilters()) return false;
    if (!this.#hasNextPage()) return false;
    return this.#getVisibleItemsCount() <= visibleCountBefore;
  }

  async #waitForClientSideFilters() {
    if (!this.#hasActiveCustomFilters()) return;

    window.customCollectionFilters?.refresh?.();

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }
}
